import { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, CircleHelp, TriangleAlert, X } from 'lucide-react';
import { getAuth } from 'firebase/auth';
import { ResolveUserIdByEmail, GetAdminBusinessHealth, GetAdminServiceHealth, GetAdminUserWallet, AdminAdjustWallet, AdminRefundWallet } from '../../Client/ManagementServiceClient';

const IMPERSONATE_USER_STORAGE_KEY = 'vixo_impersonate_user_id';
const IMPERSONATE_EMAIL_STORAGE_KEY = 'vixo_impersonate_user_email';
const IMPERSONATION_CHANGED_EVENT = 'vixo-impersonation-changed';

const HEALTH_REFRESH_MS = 30000;

const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
// A count that could not be read is not zero -- an unanswered query renders as
// an em dash so it is never mistaken for "nothing is wrong".
const num = (n) => (n === null || n === undefined ? '—' : Number(n).toLocaleString());
const pct = (n) => (n === null || n === undefined ? '—' : `${Number(n).toFixed(1)}%`);
const isoDaysAgo = (days) => {
    const d = new Date();
    d.setUTCDate(d.getUTCDate() - days);
    return d.toISOString().slice(0, 10);
};
const todayIso = () => new Date().toISOString().slice(0, 10);
const duration = (seconds) => {
    if (seconds === null || seconds === undefined) return '—';
    const s = Math.round(Number(seconds));
    if (s < 60) return `${s}s`;
    if (s < 3600) return `${Math.round(s / 60)}m`;
    if (s < 86400) return `${(s / 3600).toFixed(1)}h`;
    return `${(s / 86400).toFixed(1)}d`;
};

// Validated (CVD-safe, both modes) categorical pair — see dataviz validator.
// Income = blue, Spend = amber. Series identity also carries a legend + direct
// end-labels + a table, so it never relies on color alone.
//
// The status steps are the fixed status palette (good/warning/critical) — they
// are never used for a data series, and every one of them ships beside an icon
// and a word, since two of them are sub-3:1 on a light surface by design.
const SERIES_STYLE = `
.vixo-billing { --bill-income:var(--sb-blue); --bill-spend:var(--sb-caution); --biz-activity:var(--sb-blue); }
.vixo-billing { --st-good:var(--sb-good); --st-warning:var(--sb-caution); --st-critical:var(--sb-marker-text); }
`;

const STATUS_META = {
    ok: { Icon: Check, label: 'Operational', chip: 'OK', varName: '--st-good' },
    degraded: { Icon: TriangleAlert, label: 'Degraded', chip: 'Warn', varName: '--st-warning' },
    down: { Icon: X, label: 'Down', chip: 'Fail', varName: '--st-critical' },
    // Could not be checked from here — deliberately neutral, not a warning.
    unknown: { Icon: CircleHelp, label: 'Unverified', chip: 'n/a', varName: '--ui-text-muted' },
};

function StatusPill({ status, short = false }) {
    const meta = STATUS_META[status] || STATUS_META.unknown;
    return (
        <span
            style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '2px 10px', borderRadius: 'var(--sb-corner)',
                border: `1px solid var(${meta.varName})`, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
            }}
        >
            <meta.Icon size={13} aria-hidden="true" style={{ color: `var(${meta.varName})` }} />
            <span>{short ? meta.chip : meta.label}</span>
        </span>
    );
}

function BillingChart({ series }) {
    const [hover, setHover] = useState(null);

    const W = 760;
    const H = 280;
    const padL = 56;
    const padR = 92;
    const padT = 16;
    const padB = 34;
    const plotW = W - padL - padR;
    const plotH = H - padT - padB;

    const n = series.length;
    const maxV = Math.max(
        1,
        ...series.map((d) => Math.max(Number(d.income_usd || 0), Number(d.spend_usd || 0)))
    );
    // Round the axis top up to a "nice" number for readable gridlines.
    const niceTop = (() => {
        const pow = Math.pow(10, Math.floor(Math.log10(maxV)));
        const step = pow * (maxV / pow > 5 ? 2 : maxV / pow > 2 ? 1 : 0.5);
        return Math.ceil(maxV / step) * step;
    })();

    const x = (i) => padL + (n <= 1 ? plotW / 2 : (i / (n - 1)) * plotW);
    const y = (v) => padT + plotH - (Number(v || 0) / niceTop) * plotH;

    const line = (key) => series.map((d, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(d[key]).toFixed(1)}`).join(' ');

    const gridLines = 4;
    const grid = Array.from({ length: gridLines + 1 }, (_, i) => (niceTop / gridLines) * i);
    const xTicks = n <= 1 ? [0] : [0, Math.floor((n - 1) / 2), n - 1];

    const onMove = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const frac = (e.clientX - rect.left) / rect.width;
        const idx = Math.max(0, Math.min(n - 1, Math.round(frac * (n - 1))));
        setHover(idx);
    };

    const last = series[n - 1] || { income_usd: 0, spend_usd: 0 };

    return (
        <svg
            viewBox={`0 0 ${W} ${H}`}
            width="100%"
            role="img"
            aria-label="Daily income versus spend"
            style={{ display: 'block', maxWidth: '100%', cursor: 'crosshair' }}
            onMouseMove={onMove}
            onMouseLeave={() => setHover(null)}
        >
            {/* gridlines + y labels */}
            {grid.map((v, i) => (
                <g key={`g${i}`}>
                    <line x1={padL} x2={W - padR} y1={y(v)} y2={y(v)} stroke="var(--ui-border, #333)" strokeWidth="1" opacity="0.5" />
                    <text x={padL - 8} y={y(v) + 4} textAnchor="end" fontSize="11" fill="var(--ui-text-muted, #9aa)">{usd(v)}</text>
                </g>
            ))}

            {/* x labels */}
            {xTicks.map((i) => (
                <text key={`x${i}`} x={x(i)} y={H - 12} textAnchor="middle" fontSize="11" fill="var(--ui-text-muted, #9aa)">
                    {series[i]?.date?.slice(5)}
                </text>
            ))}

            {/* series */}
            <path d={line('spend_usd')} fill="none" stroke="var(--bill-spend)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
            <path d={line('income_usd')} fill="none" stroke="var(--bill-income)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />

            {/* direct end-labels (secondary encoding for the amber contrast WARN) */}
            <text x={W - padR + 8} y={y(last.income_usd) + 4} fontSize="11" fontWeight="600" fill="var(--bill-income)">Income</text>
            <text x={W - padR + 8} y={y(last.spend_usd) + 4} fontSize="11" fontWeight="600" fill="var(--bill-spend)">Spend</text>

            {/* hover crosshair + markers + tooltip */}
            {hover != null && series[hover] && (
                <g>
                    <line x1={x(hover)} x2={x(hover)} y1={padT} y2={padT + plotH} stroke="var(--ui-text-muted, #9aa)" strokeWidth="1" opacity="0.5" />
                    <circle cx={x(hover)} cy={y(series[hover].income_usd)} r="3.5" fill="var(--bill-income)" stroke="var(--sb-g1)" strokeWidth="1.5" />
                    <circle cx={x(hover)} cy={y(series[hover].spend_usd)} r="3.5" fill="var(--bill-spend)" stroke="var(--sb-g1)" strokeWidth="1.5" />
                    <g transform={`translate(${Math.min(x(hover) + 8, W - padR - 120)}, ${padT + 6})`}>
                        <rect width="150" height="52" rx="6" fill="var(--ui-surface-strong, #222)" stroke="var(--ui-border, #333)" />
                        <text x="8" y="16" fontSize="11" fill="var(--ui-text, #eee)" fontWeight="600">{series[hover].date}</text>
                        <text x="8" y="32" fontSize="11" fill="var(--ui-text-muted, #9aa)">Income: {usd(series[hover].income_usd)}</text>
                        <text x="8" y="46" fontSize="11" fill="var(--ui-text-muted, #9aa)">Spend: {usd(series[hover].spend_usd)}</text>
                    </g>
                </g>
            )}
        </svg>
    );
}

/** Active users per day. One series, one hue, so no legend — the heading names
 *  it. Bars sit on the baseline with a 2px surface gap between them. */
function ActivityChart({ series }) {
    const [hover, setHover] = useState(null);

    const W = 760;
    const H = 180;
    const padL = 44;
    const padR = 16;
    const padT = 14;
    const padB = 28;
    const plotW = W - padL - padR;
    const plotH = H - padT - padB;

    const n = series.length;
    const maxV = Math.max(1, ...series.map((d) => Number(d.active_users || 0)));
    const niceTop = Math.max(1, Math.ceil(maxV / 4) * 4);

    const slot = plotW / Math.max(1, n);
    const barW = Math.max(2, slot - 2); // 2px surface gap between adjacent bars
    const x = (i) => padL + i * slot + (slot - barW) / 2;
    const y = (v) => padT + plotH - (Number(v || 0) / niceTop) * plotH;

    const grid = [0, niceTop / 2, niceTop];
    const xTicks = n <= 1 ? [0] : [0, Math.floor((n - 1) / 2), n - 1];

    return (
        <svg
            viewBox={`0 0 ${W} ${H}`}
            width="100%"
            role="img"
            aria-label="Active users per day"
            style={{ display: 'block', maxWidth: '100%' }}
            onMouseLeave={() => setHover(null)}
        >
            {grid.map((v, i) => (
                <g key={`ag${i}`}>
                    <line x1={padL} x2={W - padR} y1={y(v)} y2={y(v)} stroke="var(--ui-border, #333)" strokeWidth="1" opacity="0.5" />
                    <text x={padL - 8} y={y(v) + 4} textAnchor="end" fontSize="11" fill="var(--ui-text-muted, #9aa)">{Math.round(v)}</text>
                </g>
            ))}

            {series.map((d, i) => {
                const h = Math.max(0, padT + plotH - y(d.active_users));
                return (
                    <rect
                        key={d.date}
                        x={x(i)}
                        y={y(d.active_users)}
                        width={barW}
                        height={h}
                        rx={Math.min(4, barW / 2)}
                        fill="var(--biz-activity)"
                        opacity={hover === null || hover === i ? 1 : 0.55}
                        onMouseEnter={() => setHover(i)}
                    />
                );
            })}

            {xTicks.map((i) => (
                <text key={`ax${i}`} x={x(i) + barW / 2} y={H - 10} textAnchor="middle" fontSize="11" fill="var(--ui-text-muted, #9aa)">
                    {series[i]?.date?.slice(5)}
                </text>
            ))}

            {hover != null && series[hover] && (
                <g transform={`translate(${Math.min(x(hover) + 8, W - padR - 150)}, ${padT + 4})`}>
                    <rect width="146" height="52" rx="6" fill="var(--ui-surface-strong, #222)" stroke="var(--ui-border, #333)" />
                    <text x="8" y="16" fontSize="11" fill="var(--ui-text, #eee)" fontWeight="600">{series[hover].date}</text>
                    <text x="8" y="32" fontSize="11" fill="var(--ui-text-muted, #9aa)">Active users: {num(series[hover].active_users)}</text>
                    <text x="8" y="46" fontSize="11" fill="var(--ui-text-muted, #9aa)">Billable runs: {num(series[hover].events)}</text>
                </g>
            )}
        </svg>
    );
}

function Tile({ label, value, accentVar, hint }) {
    return (
        <div style={{ flex: 1, minWidth: 140, border: '1px solid var(--sb-line)', borderRadius: 'var(--sb-corner)', padding: '12px 14px', background: 'var(--sb-g2)' }}>
            <div style={{ fontSize: 12, color: 'var(--ui-text-muted, #9aa)' }}>{label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4, color: accentVar ? `var(${accentVar})` : 'var(--ui-text, #eee)' }}>{value}</div>
            {hint && <div style={{ fontSize: 11, color: 'var(--ui-text-muted, #9aa)', marginTop: 2 }}>{hint}</div>}
        </div>
    );
}

/** A count that should be zero. Non-zero turns critical and says why, so the
 *  colour is a second reading of the label rather than the only one. */
function AlarmTile({ label, value, hint }) {
    const raised = Number(value || 0) > 0;
    return <Tile label={label} value={num(value)} accentVar={raised ? '--st-critical' : undefined} hint={raised ? hint : undefined} />;
}

const th = { padding: '4px 8px', textAlign: 'left' };
const td = { padding: '4px 8px' };

function ServiceHealth() {
    const [health, setHealth] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const auth = getAuth();
            const idToken = await auth.currentUser.getIdToken();
            const { status, myJson } = await GetAdminServiceHealth(idToken);
            if (status !== 200) {
                setError(myJson?.detail || 'Failed to load service health.');
                return;
            }
            setHealth(myJson);
        } catch (err) {
            setError(err?.message || 'Failed to load service health.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        const timer = setInterval(load, HEALTH_REFRESH_MS);
        return () => clearInterval(timer);
    }, [load]);

    const queue = health?.queue || {};
    const failures = health?.failures || {};
    const integrity = health?.billing_integrity || {};
    const busyTypes = (queue.by_type || []).filter((t) => t.pending || t.in_progress);

    return (
        <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <h4 style={{ margin: 0 }}>Service health</h4>
                {health && <StatusPill status={health.status} />}
                <span style={{ fontSize: 12, color: 'var(--ui-text-muted, #667)' }}>
                    {health ? `${health.environment} · as of ${new Date(health.generated_at).toLocaleTimeString()}` : ''}
                </span>
                <button className="btn btn-sm btn-secondary" onClick={load} disabled={loading} style={{ marginLeft: 'auto' }}>
                    {loading ? 'Checking…' : 'Re-check'}
                </button>
            </div>
            <p style={{ marginTop: 4, color: 'var(--ui-text-muted, #667)' }}>
                Dependencies probed live; queue and billing figures read from what the system already writes. Auto-refreshes every 30s.
            </p>

            {error && <div style={{ color: 'var(--sb-marker-text)', marginBottom: 10 }}>{error}</div>}

            {health && (
                <>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 14 }}>
                        <thead>
                            <tr style={{ color: 'var(--ui-text-muted, #667)' }}>
                                <th style={th}>Dependency</th>
                                <th style={th}>State</th>
                                <th style={th}>Latency</th>
                                <th style={th}>Detail</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(health.dependencies || []).map((d) => (
                                <tr key={d.name}>
                                    <td style={td}>{d.name}{d.critical ? ' *' : ''}</td>
                                    <td style={td}><StatusPill status={d.status} short /></td>
                                    <td style={td}>{d.latency_ms} ms</td>
                                    <td style={{ ...td, color: 'var(--ui-text-muted, #667)' }}>{d.note || ''}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <div style={{ fontSize: 12, color: 'var(--ui-text-muted, #667)', marginBottom: 10 }}>
                        * a failure here takes the whole service down, not just one feature.
                    </div>

                    <h5 style={{ margin: '12px 0 6px' }}>Async work queue</h5>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                        <Tile label="Pending" value={num(queue.pending)} />
                        <Tile label="In progress" value={num(queue.in_progress)} />
                        <AlarmTile label="Stuck" value={queue.stuck} hint={`held past the ${duration(health.thresholds?.lease_seconds)} lease`} />
                        <AlarmTile label="Overdue" value={queue.overdue} hint={`oldest ${duration(queue.oldest_overdue_seconds)} late`} />
                        <AlarmTile label="Failed (24h)" value={failures.total_24h} hint="gave up after retries" />
                    </div>
                    {queue.note && <div style={{ fontSize: 12, color: 'var(--sb-marker-text)', marginBottom: 8 }}>{queue.note}</div>}
                    {queue.truncated && (
                        <div style={{ fontSize: 12, color: 'var(--ui-text-muted, #667)', marginBottom: 8 }}>
                            Per-type breakdown is capped at the first 500 tasks per state; the totals above are exact.
                        </div>
                    )}

                    {busyTypes.length > 0 && (
                        <details style={{ marginBottom: 12 }}>
                            <summary style={{ cursor: 'pointer', fontSize: 13 }}>Queue by task type</summary>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 8 }}>
                                <thead>
                                    <tr style={{ color: 'var(--ui-text-muted, #667)' }}>
                                        <th style={th}>Type</th><th style={th}>Pending</th><th style={th}>In progress</th><th style={th}>Stuck</th><th style={th}>Overdue</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {busyTypes.map((t) => (
                                        <tr key={t.type}>
                                            <td style={td}>{t.type}</td>
                                            <td style={td}>{num(t.pending)}</td>
                                            <td style={td}>{num(t.in_progress)}</td>
                                            <td style={{ ...td, color: t.stuck ? 'var(--st-critical)' : undefined }}>{num(t.stuck)}</td>
                                            <td style={{ ...td, color: t.overdue ? 'var(--st-critical)' : undefined }}>{num(t.overdue)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </details>
                    )}

                    {(failures.samples || []).length > 0 && (
                        <details style={{ marginBottom: 12 }}>
                            <summary style={{ cursor: 'pointer', fontSize: 13 }}>Recent failures</summary>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 8 }}>
                                <thead>
                                    <tr style={{ color: 'var(--ui-text-muted, #667)' }}>
                                        <th style={th}>Type</th><th style={th}>Entity</th><th style={th}>User</th><th style={th}>Retries</th><th style={th}>Failed at</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {failures.samples.map((f, i) => (
                                        <tr key={`${f.entity_id}-${i}`}>
                                            <td style={td}>{f.type}</td>
                                            <td style={td}>{f.entity_id || '—'}</td>
                                            <td style={td}>{f.user_id || '—'}</td>
                                            <td style={td}>{num(f.retry_count)}</td>
                                            <td style={td}>{new Date(f.failed_at).toLocaleString()}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </details>
                    )}

                    <h5 style={{ margin: '12px 0 6px' }}>Billing integrity</h5>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        <Tile label="Usage awaiting BigQuery" value={num(integrity.usage_pending)} />
                        <AlarmTile label="Usage never published" value={integrity.usage_failed} hint="delivered work that will not be invoiced" />
                        <Tile label="Open wallet holds" value={num(integrity.open_holds)} />
                    </div>
                </>
            )}
        </>
    );
}

function BusinessHealth({ startDate, endDate, onStartDate, onEndDate }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const auth = getAuth();
            const idToken = await auth.currentUser.getIdToken();
            const { status, myJson } = await GetAdminBusinessHealth(idToken, startDate, endDate);
            if (status !== 200) {
                setError(myJson?.detail || 'Failed to load business health.');
                return;
            }
            setData(myJson);
        } catch (err) {
            setError(err?.message || 'Failed to load business health.');
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate]);

    useEffect(() => {
        load();
    }, [load]);

    const series = useMemo(() => (Array.isArray(data?.series) ? data.series : []), [data]);
    const revenue = data?.revenue || {};
    const customers = data?.customers || {};
    const activity = data?.activity || {};
    const notes = [revenue.income_note, revenue.spend_note, activity.note, customers.wallet_note].filter(Boolean);

    return (
        <>
            <h4 style={{ marginBottom: 4 }}>Business health</h4>
            <p style={{ marginTop: 0, color: 'var(--ui-text-muted, #667)' }}>
                Collected revenue (Stripe) against infrastructure spend (GCP billing export), plus who is using the product.
            </p>

            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
                <label style={{ fontSize: 13 }}>From <input type="date" value={startDate} max={endDate} onChange={(e) => onStartDate(e.target.value)} /></label>
                <label style={{ fontSize: 13 }}>To <input type="date" value={endDate} min={startDate} max={todayIso()} onChange={(e) => onEndDate(e.target.value)} /></label>
                <button className="btn btn-sm btn-primary" onClick={load} disabled={loading}>
                    {loading ? 'Loading…' : 'Refresh'}
                </button>
            </div>

            {error && <div style={{ color: 'var(--sb-marker-text)', marginBottom: 10 }}>{error}</div>}

            {data && (
                <>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                        <Tile label="Collected (Stripe)" value={usd(revenue.collected_usd)} accentVar="--bill-income" />
                        <Tile label="Infra spend (GCP)" value={usd(revenue.infra_spend_usd)} accentVar="--bill-spend" />
                        <Tile label="Net" value={usd(revenue.net_usd)} />
                        <Tile label="Gross margin" value={pct(revenue.gross_margin_pct)} hint="of collected revenue" />
                        <Tile label="Charged to users" value={usd(revenue.charged_usage_usd)} hint="metered usage in range" />
                    </div>

                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
                        <Tile label="Active users" value={num(customers.active_users)} hint="ran billable work in range" />
                        <Tile label="New users" value={num(customers.new_users)} />
                        <Tile label="Total users" value={num(customers.total_users)} />
                        <Tile label="With payment method" value={num(customers.with_payment_method)} />
                        <Tile label="Revenue / active user" value={usd(customers.arpu_usd)} />
                        <Tile label="Unspent wallet balance" value={usd(customers.wallet_balance_usd)} hint="collected, not yet delivered" />
                    </div>

                    {series.length > 0
                        ? <BillingChart series={series} />
                        : <p style={{ color: 'var(--ui-text-muted, #667)' }}>No billing data in this range.</p>}

                    {series.length > 0 && (
                        <>
                            <h5 style={{ margin: '16px 0 4px' }}>Active users per day</h5>
                            <ActivityChart series={series} />
                        </>
                    )}

                    {(activity.by_flow || []).length > 0 && (
                        <>
                            <h5 style={{ margin: '16px 0 4px' }}>What users are running</h5>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                <thead>
                                    <tr style={{ color: 'var(--ui-text-muted, #667)' }}>
                                        <th style={th}>Flow</th><th style={th}>Runs</th><th style={th}>Users</th><th style={th}>Charged</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {activity.by_flow.map((f) => (
                                        <tr key={f.flow}>
                                            <td style={td}>{f.flow}</td>
                                            <td style={td}>{num(f.events)}</td>
                                            <td style={td}>{num(f.users)}</td>
                                            <td style={td}>{usd(f.charged_usd)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </>
                    )}

                    {notes.length > 0 && (
                        <div style={{ marginTop: 10, fontSize: 12, color: 'var(--ui-text-muted, #667)' }}>
                            {notes.map((note, i) => <div key={i}>{note}</div>)}
                        </div>
                    )}

                    {/* Table view — accessibility + relief for the light-mode amber contrast WARN */}
                    {series.length > 0 && (
                        <details style={{ marginTop: 12 }}>
                            <summary style={{ cursor: 'pointer', fontSize: 13 }}>Show data table</summary>
                            <div style={{ maxHeight: 240, overflow: 'auto', marginTop: 8 }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                    <thead>
                                        <tr style={{ color: 'var(--ui-text-muted, #667)' }}>
                                            <th style={th}>Date</th><th style={th}>Income</th><th style={th}>Spend</th><th style={th}>Active users</th><th style={th}>Runs</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {series.map((d) => (
                                            <tr key={d.date}>
                                                <td style={td}>{d.date}</td>
                                                <td style={td}>{usd(d.income_usd)}</td>
                                                <td style={td}>{usd(d.spend_usd)}</td>
                                                <td style={td}>{num(d.active_users)}</td>
                                                <td style={td}>{num(d.events)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </details>
                    )}
                </>
            )}
        </>
    );
}

// A balance is real money, so every action here states what it is about to do
// in dollars and makes the operator type a reason first. The reason is not
// decoration: it is the audit trail, and it is what someone reads months later
// when asking why an account was credited.
function WalletAdmin() {
    const [email, setEmail] = useState('');
    const [wallet, setWallet] = useState(null);
    const [busy, setBusy] = useState('');
    const [reason, setReason] = useState('');
    const [amount, setAmount] = useState('');
    const [confirming, setConfirming] = useState('');
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    const token = async () => await getAuth().currentUser.getIdToken();

    const find = async () => {
        const wanted = String(email || '').trim().toLowerCase();
        if (!wanted) { setError('Enter a user email.'); return; }
        setBusy('find'); setError(''); setMessage(''); setWallet(null); setConfirming('');
        try {
            const idToken = await token();
            const found = await ResolveUserIdByEmail(idToken, wanted);
            if (found.status !== 200 || !found.myJson?.user_id) {
                setError(found.myJson?.detail || 'User not found.');
                return;
            }
            const loaded = await GetAdminUserWallet(idToken, found.myJson.user_id);
            if (loaded.status !== 200) {
                setError(loaded.myJson?.detail || 'Could not read that wallet.');
                return;
            }
            setWallet(loaded.myJson);
        } catch (err) {
            setError(err?.message || 'Lookup failed.');
        } finally {
            setBusy('');
        }
    };

    const run = async (action) => {
        setBusy(action); setError(''); setMessage('');
        try {
            const idToken = await token();
            const { status, myJson } = action === 'refund'
                ? await AdminRefundWallet(idToken, wallet.user_id, reason, wallet.refundable_usd)
                : await AdminAdjustWallet(idToken, wallet.user_id, action, amount, reason);

            if (status !== 200) {
                setError(myJson?.detail || 'The wallet was not changed.');
                return;
            }
            const moved = amount;
            setWallet(myJson);
            setReason(''); setAmount(''); setConfirming('');
            if (action === 'refund') {
                const failures = myJson.failures || [];
                setMessage(
                    'Refunded ' + usd(myJson.refunded_usd) + ' to the card.'
                    + (failures.length ? ' ' + failures.length + ' charge(s) could not be refunded: ' + failures.join('; ') : '')
                );
            } else {
                setMessage(
                    (action === 'credit' ? 'Added ' : 'Removed ') + usd(moved)
                    + '. Balance is now ' + usd(myJson.balance?.balance_usd) + '.'
                );
            }
        } catch (err) {
            setError(err?.message || 'The wallet was not changed.');
        } finally {
            setBusy('');
        }
    };

    const reasonGiven = String(reason || '').trim().length >= 3;
    const amountGiven = Number(amount) > 0;
    const max = wallet?.adjustment_max_usd || 500;
    const amountValid = amountGiven && Number(amount) <= max;
    const notRefundable = (wallet?.balance?.available_usd || 0) - (wallet?.refundable_usd || 0);

    const confirmText = {
        credit: 'Add ' + usd(amount) + ' of credit to ' + email + '? No money changes hands, and it cannot be refunded as cash later.',
        debit: 'Remove ' + usd(amount) + ' of credit from ' + email + '? No money is returned to them. The balance may go negative, which blocks further work.',
        refund: 'Refund ' + usd(wallet?.refundable_usd) + ' to the card ' + email + ' paid with, and take ' + usd(wallet?.refund_plan?.debit_usd) + ' off their balance? This cannot be undone here.',
    }[confirming];

    const stat = (label, value) => (
        <div>
            <div style={{ fontSize: '0.8rem', opacity: 0.7 }}>{label}</div>
            <div style={{ fontSize: '1.2rem' }}>{value}</div>
        </div>
    );

    return (
        <div>
            <h4>User wallet</h4>
            <p style={{ marginTop: '4px' }}>Look up a balance, adjust it, or refund what is left to the card it was paid on.</p>

            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '12px' }}>
                <input
                    type="email"
                    placeholder="user@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') find(); }}
                    style={{ flex: 1, padding: '8px 10px' }}
                />
                <button className="btn btn-sm btn-primary" onClick={find} disabled={busy === 'find'}>
                    {busy === 'find' ? 'Looking up...' : 'Look up'}
                </button>
            </div>

            {wallet && (
                <div style={{ marginTop: '16px' }}>
                    <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                        {stat('Balance', usd(wallet.balance?.balance_usd))}
                        {stat('Held', usd(wallet.balance?.reserved_usd))}
                        {stat('Available', usd(wallet.balance?.available_usd))}
                        {stat('Refundable to card', usd(wallet.refundable_usd))}
                        {stat('Paid in / spent', usd(wallet.balance?.lifetime_topped_up_usd) + ' / ' + usd(wallet.balance?.lifetime_spent_usd))}
                    </div>

                    {notRefundable > 0.005 && (
                        <div style={{ marginTop: '8px', fontSize: '0.85rem', opacity: 0.75 }}>
                            {usd(notRefundable)} of this balance cannot go back to a card: pack bonuses are not
                            refundable, and neither is credit that was granted rather than paid for, or paid on a
                            charge Stripe will no longer refund.
                        </div>
                    )}

                    <div style={{ marginTop: '16px', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <input
                            type="text"
                            placeholder="Reason (recorded against your name)"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            style={{ flex: '2 1 280px', padding: '8px 10px' }}
                        />
                        <input
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder={'Amount (max ' + usd(max) + ')'}
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            style={{ flex: '1 1 160px', padding: '8px 10px' }}
                        />
                    </div>

                    <div style={{ marginTop: '10px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <button
                            className="btn btn-sm btn-primary"
                            onClick={() => setConfirming('credit')}
                            disabled={!!busy || !reasonGiven || !amountValid}
                        >Add credit</button>
                        <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => setConfirming('debit')}
                            disabled={!!busy || !reasonGiven || !amountValid}
                        >Remove credit</button>
                        <button
                            className="btn btn-sm btn-danger"
                            onClick={() => setConfirming('refund')}
                            disabled={!!busy || !reasonGiven || !(wallet.refundable_usd > 0)}
                        >Refund {usd(wallet.refundable_usd)} to card</button>
                    </div>

                    {amountGiven && !amountValid && (
                        <div style={{ marginTop: '8px', color: 'var(--sb-marker-text)', fontSize: '0.85rem' }}>
                            One adjustment cannot exceed {usd(max)}.
                        </div>
                    )}
                    {!reasonGiven && (
                        <div style={{ marginTop: '8px', fontSize: '0.85rem', opacity: 0.7 }}>
                            A reason is required before anything can move.
                        </div>
                    )}

                    {confirming && (
                        <div style={{ marginTop: '12px', padding: '12px', border: '1px solid var(--sb-marker)', borderRadius: 'var(--sb-corner)' }}>
                            <div>{confirmText}</div>
                            <div style={{ marginTop: '10px', display: 'flex', gap: '8px' }}>
                                <button className="btn btn-sm btn-danger" onClick={() => run(confirming)} disabled={!!busy}>
                                    {busy ? 'Working...' : 'Yes, do it'}
                                </button>
                                <button className="btn btn-sm btn-secondary" onClick={() => setConfirming('')} disabled={!!busy}>Cancel</button>
                            </div>
                        </div>
                    )}

                    {!!(wallet.ledger || []).length && (
                        <div style={{ marginTop: '18px' }}>
                            <div style={{ fontSize: '0.85rem', opacity: 0.7, marginBottom: '6px' }}>Recent movements</div>
                            <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ textAlign: 'left', opacity: 0.7 }}>
                                        <th style={{ padding: '4px 6px' }}>When</th>
                                        <th style={{ padding: '4px 6px' }}>Kind</th>
                                        <th style={{ padding: '4px 6px', textAlign: 'right' }}>Amount</th>
                                        <th style={{ padding: '4px 6px', textAlign: 'right' }}>Balance</th>
                                        <th style={{ padding: '4px 6px' }}>By / note</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {wallet.ledger.map((row, i) => (
                                        <tr key={i} style={{ borderTop: '1px solid var(--ui-border, #eee)' }}>
                                            <td style={{ padding: '4px 6px', whiteSpace: 'nowrap' }}>
                                                {String(row.created_at || '').slice(0, 19).replace('T', ' ')}
                                            </td>
                                            <td style={{ padding: '4px 6px' }}>{row.kind}</td>
                                            <td style={{ padding: '4px 6px', textAlign: 'right', color: row.amount_usd < 0 ? 'var(--sb-marker-text)' : 'var(--sb-good)' }}>
                                                {usd(row.amount_usd)}
                                            </td>
                                            <td style={{ padding: '4px 6px', textAlign: 'right' }}>{usd(row.balance_after_usd)}</td>
                                            <td style={{ padding: '4px 6px' }}>
                                                {row.actor_email ? row.actor_email + ' - ' : ''}{row.note || row.entity_flow || ''}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {message && <div style={{ marginTop: '10px', color: 'var(--sb-good)' }}>{message}</div>}
            {error && <div style={{ marginTop: '10px', color: 'var(--sb-marker-text)' }}>{error}</div>}
        </div>
    );
}


function Admin() {
    const [targetEmail, setTargetEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [activeSession, setActiveSession] = useState({ email: '', userId: '' });

    // Date range for the business view
    const [startDate, setStartDate] = useState(isoDaysAgo(29));
    const [endDate, setEndDate] = useState(todayIso());

    const loadActiveSession = () => {
        setActiveSession({
            email: String(localStorage.getItem(IMPERSONATE_EMAIL_STORAGE_KEY) || '').trim(),
            userId: String(localStorage.getItem(IMPERSONATE_USER_STORAGE_KEY) || '').trim(),
        });
    };

    useEffect(() => {
        loadActiveSession();
    }, []);

    const handleStartImpersonation = async () => {
        const email = String(targetEmail || '').trim().toLowerCase();
        if (!email) {
            setError('Please enter a user email.');
            setMessage('');
            return;
        }

        try {
            setIsSubmitting(true);
            setError('');
            setMessage('');

            const auth = getAuth();
            const idToken = await auth.currentUser.getIdToken();
            const { status, myJson } = await ResolveUserIdByEmail(idToken, email);

            if (status !== 200 || !myJson?.user_id) {
                setError(myJson?.detail || 'Failed to start impersonation session.');
                return;
            }

            localStorage.setItem(IMPERSONATE_EMAIL_STORAGE_KEY, email);
            localStorage.setItem(IMPERSONATE_USER_STORAGE_KEY, String(myJson.user_id));
            window.dispatchEvent(new Event(IMPERSONATION_CHANGED_EVENT));
            loadActiveSession();
            setMessage(`Impersonation started for ${email} (${myJson.user_id}).`);
        } catch (err) {
            setError(err?.message || 'Failed to start impersonation session.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleStopImpersonation = () => {
        localStorage.removeItem(IMPERSONATE_USER_STORAGE_KEY);
        localStorage.removeItem(IMPERSONATE_EMAIL_STORAGE_KEY);
        window.dispatchEvent(new Event(IMPERSONATION_CHANGED_EVENT));
        loadActiveSession();
        setMessage('Impersonation stopped.');
        setError('');
    };

    const cardStyle = { maxWidth: 820, marginTop: 16, padding: 16, border: '1px solid var(--sb-line)', borderRadius: 'var(--sb-corner)', background: 'var(--sb-g2)' };

    return (
        <div className="vixo-billing">
            <style>{SERIES_STYLE}</style>
            <h2>Admin</h2>
            <p>This page is visible only to Admin.</p>

            <div style={cardStyle}>
                <ServiceHealth />
            </div>

            <div style={cardStyle}>
                <BusinessHealth
                    startDate={startDate}
                    endDate={endDate}
                    onStartDate={setStartDate}
                    onEndDate={setEndDate}
                />
            </div>

            <div style={cardStyle}>
                <WalletAdmin />
            </div>

            {/* Impersonation */}
            <div style={cardStyle}>
                <h4>Impersonation</h4>
                <p style={{ marginTop: '4px' }}>Start an impersonation session by user email.</p>

                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '12px' }}>
                    <input
                        type="email"
                        placeholder="user@example.com"
                        value={targetEmail}
                        onChange={(e) => setTargetEmail(e.target.value)}
                        style={{ flex: 1, padding: '8px 10px' }}
                    />
                    <button
                        className="btn btn-sm btn-primary"
                        onClick={handleStartImpersonation}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Starting...' : 'Start Impersonation'}
                    </button>
                    <button
                        className="btn btn-sm btn-secondary"
                        onClick={handleStopImpersonation}
                    >
                        Stop
                    </button>
                </div>

                <div style={{ marginTop: '10px', fontSize: '0.9rem' }}>
                    <div><strong>Active email:</strong> {activeSession.email || 'None'}</div>
                    <div><strong>Active userId:</strong> {activeSession.userId || 'None'}</div>
                </div>

                {message && (
                    <div style={{ marginTop: '10px', color: 'var(--sb-good)' }}>{message}</div>
                )}
                {error && (
                    <div style={{ marginTop: '10px', color: 'var(--sb-marker-text)' }}>{error}</div>
                )}
            </div>
        </div>
    );
}

export { Admin };
